import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Truck, Search, Package } from 'lucide-react';
import { motion } from 'framer-motion';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Tracking Modal State
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingId, setTrackingId] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('skynet');

  useEffect(() => {
    if (location.hash === '#track-shipment') {
      setShowTrackingModal(true);
      // Reset hash so it can be triggered again
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  /* ── Tracking Logic ── */
  const handleTrackOrder = () => {
    if (!trackingId.trim()) return;

    const id = trackingId.trim();
    let url = '';

    switch (selectedCarrier) {
      case 'skynet':
        url = `https://www.skynetww.com/track?id=${id}`;
        break;
      case 'dhl':
        url = `https://www.dhl.com/global-en/home/tracking/tracking-express.html?submit=1&tracking-id=${id}`;
        break;
      case 'fedex':
        url = `https://www.fedex.com/fedextrack/?trknbr=${id}`;
        break;
      case 'ups':
        url = `https://www.ups.com/track?tracknum=${id}`;
        break;
      default:
        url = `https://www.skynetww.com/track?id=${id}`;
    }

    window.open(url, '_blank');
    setShowTrackingModal(false);
    setTrackingId('');
  };

  const handleTrackKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTrackOrder();
  };

  return (
    <>
      <nav style={{
        width: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: 0,
        margin: 0
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 1.5rem',
          margin: '0 auto',
          width: '100%',
          maxWidth: '90%'
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            margin: 0,
            padding: 0,
            flexShrink: 0,
            maxWidth: '60%'
          }}>
            <img
              src="/images/logo_pnfly.png"
              alt="Pack & Fly Logo"
              className="navbar-logo"
              style={{
                height: '4.5rem',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                margin: 0,
                padding: 0,
                display: 'block'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const truckIcon = target.nextElementSibling as HTMLElement;
                if (truckIcon) {
                  truckIcon.style.display = 'block';
                  truckIcon.style.width = '3rem';
                  truckIcon.style.height = '3rem';
                  truckIcon.style.color = '#2563eb';
                }
              }}
            />
            <Truck style={{
              display: 'none',
              width: '3rem',
              height: '3rem',
              color: '#2563eb'
            }} />
          </Link>

          {/* Desktop Menu */}
          <div className="nav-menu" style={{ flexWrap: 'wrap' }}>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <button
              className="btn btn-sm btn-secondary"
              style={{
                marginLeft: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: 'var(--shadow-md)',
                fontWeight: 600
              }}
              onClick={() => setShowTrackingModal(true)}
            >
              <Package size={16} />
              Track Shipment
            </button>
          </div>

          <div className="nav-toggle" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X /> : <Menu />}
          </div>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`nav-mobile ${isOpen ? 'show' : ''}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: isOpen ? 1 : 0, height: isOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link-mobile ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <button
            className="nav-link-mobile"
            style={{
              width: '100%',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--primary-color)',
              fontWeight: 600
            }}
            onClick={() => {
              setIsOpen(false);
              setShowTrackingModal(true);
            }}
          >
            <Package size={18} />
            Track Shipment
          </button>
        </motion.div>
      </nav>

      {/* ===== TRACK ORDER MODAL ===== */}
      {showTrackingModal && (
        <div className="modal-overlay" onClick={() => setShowTrackingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Track Your Shipment</h3>
              <button className="modal-close" onClick={() => setShowTrackingModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Select your carrier and enter the tracking number to get real-time status.</p>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Select Carrier</label>
                <select
                  value={selectedCarrier}
                  onChange={(e) => setSelectedCarrier(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '1rem'
                  }}
                >
                  <option value="skynet">Skynet Worldwide Express</option>
                  <option value="dhl">DHL Express</option>
                  <option value="fedex">FedEx</option>
                  <option value="ups">UPS</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Tracking Number / AWB</label>
                <input
                  type="text"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  onKeyDown={handleTrackKeyDown}
                  placeholder="Enter Tracking ID"
                  className="tracking-input"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div className="modal-buttons">
                <button onClick={handleTrackOrder} className="btn btn-primary" disabled={!trackingId.trim()}>
                  <Search size={16} /> Track Now
                </button>
                <button onClick={() => setShowTrackingModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;